import HoldingsHeader from './HoldingsHeader';
import HoldingsRow from './HoldingsRow';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { getHoldings } from '../../services/apis/holdings';
import { setHoldings, Holding, setHoldingsLoading, setHoldingsError, setPrevHoldingsTradingAccountId } from '../../reducer/holdings.reducer';
import { Paper, Typography, Box, Grid, Divider, Stack } from '@mui/material'
import { RootState } from '../../store.ts'
import { WebSocketContext } from '../webSocketConnection/WebSocket.tsx'
import { WebSocketResponseMessage, WebSocketResponseMessage_SecuritySnapshotSubscriptionResponse } from '../../proto/generated/websocket.ts'
import { Security, SecuritySnapshot, SecuritySnapshots } from '../../proto/generated/security.ts'
import { getSecurityKey } from '../../util/security.ts'
import { fetchSecuritySnapshotsBulk } from '../../services/apis/candles.ts'
import websocketActions from '../../actions/websocket.actions.ts'
import { subscriptionTags } from '../../assets/constants.ts'
import Holdings from '../../assets/icons/Holdings.tsx'
import EmptyView from '../postionsWidget/EmptyView.tsx'
import { TradingAccountStatus } from '../../proto/generated/trading-bridge.ts';
import ReconnectView from '../common/ReconnectView.tsx';
import ReconnectTradingAccount from '../common/ReconnectTradingAccount.tsx';
import { getOrderBooksBulk } from '../../util/tradepanel.ts';
import { OrderType, TransactionType, Product, Broker_OrderType } from '../../proto/generated/order'
import { setQuickOrderData } from '../../reducer/orders.reducer';
import TradingAccountSelector from '../tradingAccountStatus/TradingAccountSelector.tsx';
import { selectPrimaryTradingAccount } from '../../selectors/user.ts';
import { selectHoldings, selectHoldingsLoading, selectHoldingsPrevTradingAccount } from '../../selectors/holdings.ts';

const HoldingsWidget = () => {
    const dispatch = useDispatch();
    const holdings = useSelector(selectHoldings)
    const loading = useSelector(selectHoldingsLoading)
    const prevTradingAccountRef = useSelector(selectHoldingsPrevTradingAccount)
    const primaryTradingAccount = useSelector(selectPrimaryTradingAccount);
    const ws = useContext(WebSocketContext);
    const [securitySnapshotsSubscriptionResponse, setSecuritySnapshotSubscriptionResponses] = useState<WebSocketResponseMessage_SecuritySnapshotSubscriptionResponse[]>([])
    const [securitySnapshots, setSecuritySnapshots] = useState<SecuritySnapshot[]>([])
    const [securityCodeToSnapshot, setSecurityCodeToSnapshot] = useState<Map<string, SecuritySnapshot>>(new Map());
    const securityCodeToPreviousClose = useRef<Map<string, number>>(new Map<string, number>())
    const [snapshotsReady, setSnapshotsReady] = useState(false);
    // const prevTradingAccountRef = useRef<{ id: string | null; status: TradingAccountStatus | null }>({ id: null, status: null });
    const [showReconnect, setShowReconnect] = useState(false);
    const prevHoldingsTradingAccount = useRef<string | null>(null);

    const fetchSecuritySnapshotBulk = useCallback(
        async (securities: Security[]) => {
            try {
                const result: SecuritySnapshots = await fetchSecuritySnapshotsBulk({
                    securities: Array.from(securities),
                    dispatch: dispatch
                })
                const noPrevCloseSecurities = result.securitySnapshots.filter(snap => (snap.previousClose === 0 || snap.previousClose === undefined) && snap.security !== undefined).map(s => s.security)
                if (noPrevCloseSecurities.length > 0) {
                    const marketDepth = await getOrderBooksBulk(noPrevCloseSecurities)
                    marketDepth.forEach(md => {
                        const snap = result.securitySnapshots.find(snap => snap.security?.code === md.security?.code && snap.security?.exchange === md.security?.exchange)
                        if (snap && (snap.previousClose === 0 || snap.previousClose === undefined) && md.close !== undefined) {
                            snap.previousClose = md.close
                        }
                    })
                }

                setSecuritySnapshots(result.securitySnapshots)
            } catch (error) {
                console.error('Error fetching security data', error)
                return
            }
        },
        [dispatch]
    )

    const onWebSocketMessageReceived = useCallback((data: Uint8Array) => {
        const responseData = WebSocketResponseMessage.decode(data)
        if (responseData.securitySnapshotSubscriptionResponses.length > 0) {
            setSecuritySnapshotSubscriptionResponses(responseData.securitySnapshotSubscriptionResponses)
        }
    }, [])

    const holdingSecurities = useMemo(() => {
        return holdings.map((holding) => holding.security).filter((security): security is Security => security !== undefined)
    }, [holdings])

    useEffect(() => {
        if (holdingSecurities.length > 0) {
            fetchSecuritySnapshotBulk(holdingSecurities)
            websocketActions.setWebsocketSubscribeList(dispatch, {
                securitySnapshotSubscriptions: [
                    {
                        tag: subscriptionTags.HOLDINGS_WIDGET_SUBSCRIPTION_TAG,
                        securities: holdingSecurities.map((s) => ({ exchange: s.exchange, code: s.code }))
                    }
                ]
            })
        }
    }, [dispatch, holdingSecurities, fetchSecuritySnapshotBulk])

    useEffect(() => {
        if (ws !== null) {
            ws!.addListener(onWebSocketMessageReceived)
        }
        return () => {
            if (ws !== null) {
                ws!.removeListener(onWebSocketMessageReceived)
            }
        }
    }, [onWebSocketMessageReceived, ws])

    useEffect(() => {
        const securitySnapshotUpdate = securitySnapshotsSubscriptionResponse.find((s) => s.tag === subscriptionTags.HOLDINGS_WIDGET_SUBSCRIPTION_TAG)
        if (securitySnapshotUpdate && securitySnapshotUpdate.securitySnapshots.length > 0) {
            const existingSecuritySnapshots = [...securitySnapshots]
            securitySnapshotUpdate.securitySnapshots.forEach((updatedSnapshot) => {
                if (!updatedSnapshot.security?.code) {
                    return
                }
                const index = existingSecuritySnapshots.findIndex((existingSnapshot) => existingSnapshot.security?.code === updatedSnapshot.security?.code)
                if (index !== -1) {
                    const existingSnapshot = existingSecuritySnapshots[index]
                    existingSecuritySnapshots[index] = {
                        ...existingSnapshot,
                        previousClose: securityCodeToPreviousClose.current.get(updatedSnapshot.security.code) ?? 0,
                        lastTradedPrice: updatedSnapshot.lastTradedPrice
                    }
                } else {
                    const update = {
                        ...updatedSnapshot,
                        previousClose: securityCodeToPreviousClose.current.get(updatedSnapshot.security.code) ?? 0
                    }
                    existingSecuritySnapshots.push(update)
                }
            })
            setSecuritySnapshots(existingSecuritySnapshots)
        }
    }, [securitySnapshotsSubscriptionResponse, securitySnapshots])

    useEffect(() => {
        if (securitySnapshots && securitySnapshots.length > 0) {
            const newSecurityCodeToSnapshot = new Map([...securityCodeToSnapshot])
            securitySnapshots.forEach((securitySnapshot) => {
                if (!securitySnapshot.security?.code) {
                    return
                }
                securityCodeToPreviousClose.current.set(securitySnapshot.security.code, securitySnapshot.previousClose)
                newSecurityCodeToSnapshot.set(getSecurityKey(securitySnapshot.security?.exchange ?? 1, securitySnapshot.security?.code ?? ''), securitySnapshot)
            })
            setSecurityCodeToSnapshot(newSecurityCodeToSnapshot)
            setSnapshotsReady(true)
        }
    }, [securitySnapshots])

    useEffect(() => {
        let reconnectTimer: NodeJS.Timeout | undefined;
        if (!primaryTradingAccount) {
            dispatch(setHoldings([]));
            dispatch(setPrevHoldingsTradingAccountId({ id: null, status: null }))
        } else if (primaryTradingAccount?.status !== TradingAccountStatus.ACTIVE) {
            dispatch(setHoldings([]));
        }

        if (
            primaryTradingAccount?.status === TradingAccountStatus.ACTIVE &&
            (prevTradingAccountRef.id !== primaryTradingAccount?.accountId ||
                prevTradingAccountRef.status !== TradingAccountStatus.ACTIVE)
        ) {
            if (prevHoldingsTradingAccount.current !== primaryTradingAccount?.accountId) {
                (async () => {
                    dispatch(setHoldings([]));
                    dispatch(setHoldingsLoading());
                    try {
                        const data: Holding[] = await getHoldings(primaryTradingAccount?.accountId);
                        prevHoldingsTradingAccount.current = primaryTradingAccount?.accountId ?? null;
                        dispatch(setHoldings(data));
                    } catch (err: any) {
                        dispatch(setHoldingsError(err.message));
                    }
                })();
            }
        }
        reconnectTimer = setTimeout(() => {
            setShowReconnect(
                !primaryTradingAccount || primaryTradingAccount?.status !== TradingAccountStatus.ACTIVE
            );
        }, 200);
        dispatch(setPrevHoldingsTradingAccountId({ id: primaryTradingAccount?.accountId, status: primaryTradingAccount?.status }))
        return () => {
            if (reconnectTimer) clearTimeout(reconnectTimer);
        };
    }, [primaryTradingAccount?.accountId])

    let totalInvestedAmount = 0;
    let totalCurrentValue = 0;
    let totalDayPnl = 0;
    let totalPreviousCloseValue = 0;

    if (holdings) {
        holdings.forEach(holding => {
            const multiplier = holding.security?.priceMultiplier ?? 100;
            const ltp = (securityCodeToSnapshot.get(getSecurityKey(holding.security?.exchange ?? 0, holding.security?.code ?? ''))?.lastTradedPrice) / multiplier;
            const averageBuyPrice = holding.averageBuyPrice / multiplier;
            const quantity = holding.quantity;
            const previousClose = (securityCodeToSnapshot.get(getSecurityKey(holding.security?.exchange ?? 0, holding.security?.code ?? ''))?.previousClose) / multiplier;

            if (ltp && averageBuyPrice && quantity) {
                totalInvestedAmount += (averageBuyPrice * quantity);
                totalCurrentValue += (ltp * quantity);
                totalDayPnl += (ltp - previousClose) * quantity;
                totalPreviousCloseValue += previousClose * quantity;
            }
        });
    }
    let totalDayChangePercentage = (totalDayPnl / totalPreviousCloseValue) * 100;
    let totalPnl = totalCurrentValue - totalInvestedAmount;

    const handleSquareOff = useCallback((holding: Holding) => {
        const transactionType = holding.quantity > 0 ? TransactionType.SELL : TransactionType.BUY;
        if (primaryTradingAccount.accountId) {
            dispatch(setQuickOrderData({
                openDialog: true,
                mode: 'regular',
                type: 'trade',
                isSquareOffOrder: true,
                maxQuantity: holding.quantity,
                order: {
                    transactionType,
                    security: holding.security,
                    brokerOrderType: Broker_OrderType.MARKET_ORDER,
                    orderType: OrderType.NORMAL,
                    product: Product.CNC,
                    price: undefined,
                    quantity: holding.quantity,
                }
            }));
        }
    }, [dispatch, primaryTradingAccount]);

    return (
        <Paper
            sx={{
                borderTopRightRadius: '6px',
                display: 'flex',
                height: '100%',
                flexDirection: 'column'
            }}
        >
            {/* Top header bar - always visible */}
            <Grid container px={'8px'} py={0} justifyContent={'space-between'} alignItems={'center'}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Holdings sx={{ color: (theme) => theme.palette.text.secondary }} />
                    <Typography variant={'body1'}>Holdings</Typography>
                </Box>
                <TradingAccountSelector />
            </Grid>
            <Divider style={{ width: '100%' }} />
            {/* Content area */}
            {primaryTradingAccount?.status === TradingAccountStatus.ACTIVE ? loading ? (<></>) : (holdings.length === 0 ? (
                // No holdings → EmptyView centered
                (<Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <EmptyView text="No holdings. Place an order to view holdings." />
                </Box>)
            ) : (
                // Has holdings → show header + rows
                (<>
                    {snapshotsReady && totalInvestedAmount > 0 && (
                        <>
                            <HoldingsHeader
                                totalInvestedAmount={totalInvestedAmount}
                                totalCurrentValue={totalCurrentValue}
                                totalPnl={totalPnl}
                                totalDayPnl={totalDayPnl}
                                totalDayChangePercentage={isFinite(totalDayChangePercentage) ? totalDayChangePercentage : null}
                            />
                            <Divider style={{ width: '100%', height: '2px' }} />
                        </>
                    )}
                    <Box sx={{ overflowY: 'auto', flex: 1 }}>
                        {snapshotsReady &&
                            holdings.map((holding) => {
                                const multiplier = holding.security?.priceMultiplier ?? 100
                                const ltp = securityCodeToSnapshot.get(getSecurityKey(holding.security?.exchange, holding.security?.code))?.lastTradedPrice / multiplier
                                const averageBuyPrice = holding.averageBuyPrice / multiplier
                                const investedAmount = holding.quantity * averageBuyPrice
                                const pnl = (ltp - averageBuyPrice) * holding.quantity
                                const previousClose = securityCodeToSnapshot.get(getSecurityKey(holding.security?.exchange, holding.security?.code))?.previousClose / multiplier
                                const dayPnlPercentage = (previousClose && previousClose > 0)
                                    ? ((ltp - previousClose) / previousClose) * 100
                                    : null

                                return (
                                    <HoldingsRow
                                        holding={holding}
                                        key={holding.security?.code}
                                        ltp={ltp}
                                        dayPnlPercentage={dayPnlPercentage}
                                        onSquareOff={handleSquareOff}
                                    />
                                )
                            })}
                    </Box>
                </>)
            )) : (
                showReconnect && (
                    <><Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Stack justifyContent={'center'} alignItems={'center'}>
                            <ReconnectView text={primaryTradingAccount ? "Reconnect trading account to view holdings." : "Connect trading account to view holdings."} />
                            <ReconnectTradingAccount primaryTradingAccount={primaryTradingAccount} />
                        </Stack>
                    </Box>
                    </>)
            )}
        </Paper>
    );


};

export default HoldingsWidget;